import type { Column, RowData } from "@tanstack/react-table-v9";

import { downloadFile } from "@utils";

import { buildCsv, buildXlsx } from "./files";
import type { ExportCell, ExportColumn, ExportSheet } from "./files";
import { getColumnLabel, isUtilityColumn } from "../columnUtils";
import type { TableV2Features } from "../features";
import { formatCellValue, toReadable, toYesNo } from "../formatters";
import type { TableV2Instance } from "../hooks/useTableV2";
import { toNumber } from "../numbers";
import { PIVOT_LABEL_COLUMN_ID } from "../pivot/columns";
import type { PivotRowNode } from "../pivot/types";
import type { PivotController } from "../pivot/usePivotController";
import type { TableV2ColumnMeta } from "../types";

export type ExportFormat = "csv" | "xlsx";

export interface TableV2ExportOptions<TData> {
  fileName: string;
  /** Required for server tables (`rowCount` set): loads every row matching the current search, sort and filters. Without it the export fails rather than saving one page */
  fetchAllRows?: () => Promise<TData[]>;
  /** Export every column, hidden ones included, plus any other fields on the rows under their own names */
  allFields?: boolean;
}

type AnyColumn<TData extends RowData> = Column<TableV2Features, TData, unknown>;

const visibleLeafColumns = <TData extends RowData>(
  table: Pick<
    TableV2Instance<TData>,
    | "getStartVisibleLeafColumns"
    | "getCenterVisibleLeafColumns"
    | "getEndVisibleLeafColumns"
  >,
) => [
  ...table.getStartVisibleLeafColumns(),
  ...table.getCenterVisibleLeafColumns(),
  ...table.getEndVisibleLeafColumns(),
];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const toExportDate = (value: unknown, withTime: boolean): ExportCell => {
  if (typeof value === "string" && DATE_ONLY.test(value)) {
    return new Date(`${value}T00:00:00Z`);
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      withTime ? date.getHours() : 0,
      withTime ? date.getMinutes() : 0,
    ),
  );
};

export const toExportCell = (
  value: unknown,
  meta?: TableV2ColumnMeta,
): ExportCell => {
  if (value === null || value === undefined || value === "") return null;
  switch (meta?.type) {
    case "number":
    case "currency":
    case "percent":
      return toNumber(value) ?? String(value);
    case "date":
    case "datetime":
      return toExportDate(value, meta.type === "datetime");
    case "readable":
      return toReadable(value) || null;
    case "boolean":
      return toYesNo(value);
    default:
      if (
        typeof value === "number" ||
        typeof value === "boolean" ||
        value instanceof Date
      )
        return value;
      if (Array.isArray(value)) return value.join(", ");
      return typeof value === "object" ? null : String(value);
  }
};

const isExportable = <TData extends RowData>(column: AnyColumn<TData>) => {
  const meta = column.columnDef.meta;
  if (isUtilityColumn(column) || meta?.type === "actions") return false;
  if (meta?.exportable !== undefined) return meta.exportable;
  return !!(meta?.exportValue || column.accessorFn);
};

const exportColumn = <TData extends RowData>(
  column: AnyColumn<TData>,
): ExportColumn => {
  return {
    header: getColumnLabel(column),
    type: column.columnDef.meta?.type,
  };
};

// Keys on the rows that no column reads, in the order they first appear
const uncoveredFields = <TData extends RowData>(
  columns: AnyColumn<TData>[],
  rows: TData[],
) => {
  const covered = new Set(
    columns.flatMap((column) => {
      const { accessorKey } = column.columnDef as { accessorKey?: string };
      return accessorKey ? [column.id, accessorKey] : [column.id];
    }),
  );
  const fields = new Set<string>();
  rows.forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row).forEach((key) => {
        if (!covered.has(key)) fields.add(key);
      });
    }
  });
  return [...fields];
};

export const tableSheet = <TData extends RowData>(
  table: TableV2Instance<TData>,
  name: string,
  rows: TData[],
  allFields = false,
): ExportSheet => {
  const columns = (
    allFields ? table.getAllLeafColumns() : visibleLeafColumns(table)
  ).filter(isExportable);
  const extraFields = allFields ? uncoveredFields(columns, rows) : [];
  return {
    name,
    columns: [
      ...columns.map(exportColumn),
      ...extraFields.map((field) => {
        return { header: field };
      }),
    ],
    rows: rows.map((row, index) => [
      ...columns.map((column) => {
        const meta = column.columnDef.meta;
        const value = meta?.exportValue
          ? meta.exportValue(row)
          : column.accessorFn?.(row, index);
        return toExportCell(value, meta);
      }),
      ...extraFields.map((field) =>
        toExportCell((row as Record<string, unknown>)[field]),
      ),
    ]),
  };
};

const NUMERIC_TYPES = new Set(["number", "currency", "percent"]);

/** Column map for the server-side `setTableExport`: the visible, exportable columns under their labels, formatted like the table */
export const exportColumnMap = <TData extends RowData>(
  table: TableV2Instance<TData>,
) =>
  visibleLeafColumns(table)
    .filter(isExportable)
    .map((column) => {
      return {
        csvColumn: getColumnLabel(column),
        jsColumn: (row: Record<string, unknown>) => {
          const meta = column.columnDef.meta;
          const value = meta?.exportValue
            ? meta.exportValue(row)
            : column.accessorFn?.(row as TData, 0);
          return NUMERIC_TYPES.has(meta?.type ?? "")
            ? value
            : formatCellValue(value, meta);
        },
      };
    });

const pivotRowHeaders = <TData>(controller: PivotController<TData>) =>
  controller.config.rows.map(({ field, dateGrouping }) => {
    const label = controller.fields.find((f) => f.id === field)?.label ?? field;
    return dateGrouping ? `${label} (${dateGrouping})` : label;
  });

const pivotColumnHeader = (column: AnyColumn<PivotRowNode>) => {
  const parts: string[] = [];
  for (let c: AnyColumn<PivotRowNode> | undefined = column; c; c = c.parent) {
    const { header } = c.columnDef;
    if (typeof header === "string" && header) parts.unshift(header);
  }
  return parts.join(" › ") || getColumnLabel(column);
};

/** Every row level, not just the expanded ones. Server pivots include the levels already loaded */
export const pivotSheet = <TData>(
  pivot: {
    controller: PivotController<TData>;
    table: TableV2Instance<PivotRowNode>;
  },
  name: string,
): ExportSheet => {
  const { controller, table } = pivot;
  const { config, result } = controller;
  const rowHeaders = pivotRowHeaders(controller);
  const depth = Math.max(rowHeaders.length, 1);
  const valueColumns = visibleLeafColumns(table).filter(
    (column) => column.id !== PIVOT_LABEL_COLUMN_ID && column.accessorFn,
  );

  const rows: ExportCell[][] = [];
  const boldRows = new Set<number>();
  const values = (node: PivotRowNode) =>
    valueColumns.map((column) =>
      toExportCell(column.accessorFn!(node, 0), column.columnDef.meta),
    );
  const labels = (node: PivotRowNode) =>
    Array.from({ length: depth }, (_, i) => node.labels[i] ?? null);

  const visit = (nodes: PivotRowNode[]) =>
    nodes.forEach((node) => {
      if (!node.hasChildren || config.rowSubtotals) {
        if (node.hasChildren) boldRows.add(rows.length);
        rows.push([...labels(node), ...values(node)]);
      }
      if (node.children) visit(node.children);
    });
  visit(result.rows);

  if (config.rowGrandTotal && result.rows.length) {
    const total = {
      cells: result.grandTotal,
      hasChildren: false,
      labels: ["Grand total"],
    } as PivotRowNode;
    boldRows.add(rows.length);
    rows.push([...labels(total), ...values(total)]);
  }

  return {
    name,
    columns: [
      ...(rowHeaders.length ? rowHeaders : ["Rows"]).map((header) => {
        return {
          header,
        };
      }),
      ...valueColumns.map((column) => {
        return {
          header: pivotColumnHeader(column),
          type: column.columnDef.meta?.type,
        };
      }),
    ],
    rows,
    boldRows,
  };
};

const stamp = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const downloadSheet = async (
  sheet: ExportSheet,
  format: ExportFormat,
  fileName: string,
) => {
  const blob =
    format === "csv"
      ? new Blob([`﻿${buildCsv(sheet)}`], {
          type: "text/csv;charset=utf-8",
        })
      : await buildXlsx(sheet);
  const url = URL.createObjectURL(blob);
  downloadFile(url, `${fileName} ${stamp()}.${format}`);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const exportRows = <TData extends RowData>(
  table: TableV2Instance<TData>,
  fetchAllRows?: () => Promise<TData[]>,
) => {
  if (fetchAllRows) return fetchAllRows();
  const rows = table.getPrePaginatedRowModel().rows.map((row) => row.original);
  // Server tables only hold the current page
  const total = table.options.rowCount;
  if (total !== undefined && total > rows.length) {
    throw new Error(
      "TableV2 export: server tables need exportOptions.fetchAllRows to export every row",
    );
  }
  return Promise.resolve(rows);
};

export const exportTable = async <TData extends RowData>(
  table: TableV2Instance<TData>,
  format: ExportFormat,
  { fileName, fetchAllRows, allFields }: TableV2ExportOptions<TData>,
) => {
  const pivot = table.v2?.view === "pivot" ? table.v2.pivot : null;
  const sheet = pivot
    ? pivotSheet(
        pivot as {
          controller: PivotController<TData>;
          table: TableV2Instance<PivotRowNode>;
        },
        fileName,
      )
    : tableSheet(
        table,
        fileName,
        await exportRows(table, fetchAllRows),
        allFields,
      );
  await downloadSheet(sheet, format, fileName);
};
